import { ApolloError } from '@apollo/client';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { useSpeechToTextMutation } from '@generated/graphql';
import { useToast } from '@myhrbox/myhrbox-frontend-core';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseVoiceRecordedParams {
  onTranscriptionSuccess?: (transcribedText: string) => void;
  onTranscriptionError?: () => void;
}

export type UseVoiceRecordedReturn = {
  audioSrc: string | undefined;
  status: RecordStatus;
  audioRef: RefObject<HTMLAudioElement>;
  timer: number;
  isTranscribing: boolean;
  startRecord: (onChange: (value: Blob | undefined) => void) => Promise<void>;
  stopRecord: () => void;
  play: () => void;
  pause: () => void;
  removeRecorder: (onChange: (value: Blob | undefined) => void) => void;
  transcribingError?: ApolloError;
};

export enum RecordStatus {
  Ready,
  Waiting,
  Recording,
  Done,
  Pause,
  Playing,
  NotAllowed,
  Converting,
}

const useVoiceRecorded = ({
  onTranscriptionSuccess,
  onTranscriptionError,
}: UseVoiceRecordedParams = {}): UseVoiceRecordedReturn => {
  const [recorder, setRecorder] = useState<MediaRecorder>();
  const [audioSrc, setAudioSrc] = useState<string>();
  const [status, setStatus] = useState<RecordStatus>(RecordStatus.Ready);
  const [timer, setTimer] = useState<number>(0);
  const [timeout, setTimeout] = useState<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const [speechToText, { loading: isTranscribing, error: transcribingError }] =
    useSpeechToTextMutation();

  const initRecorder = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setStatus(RecordStatus.NotAllowed);
      return null;
    }

    try {
      setStatus(RecordStatus.Waiting);
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(mediaStream);
      setRecorder(mediaRecorder);
      return { mediaStream, mediaRecorder };
    } catch (err) {
      setStatus(RecordStatus.NotAllowed);
      console.error(err);
      return null;
    }
  };

  const initTimer = () => {
    let count = 0;
    setTimeout(
      setInterval(() => {
        count += 1;
        setTimer(count);
      }, 1000),
    );
  };

  const clearTimer = () => {
    if (timeout) {
      clearInterval(timeout);
      setTimeout(null);
    }
  };

  const play = () => {
    const audio = audioRef.current;
    if (!audio) return;
    console.log('readyState', audio.readyState);

    setStatus(RecordStatus.Waiting);

    const tryPlay = () => {
      audio
        .play()
        .then(() => {
          setStatus(RecordStatus.Playing);
        })
        .catch((error) => {
          console.error('play() error:', error);
          setStatus(RecordStatus.Pause); // не Playing
        });
    };

    if (audio.readyState >= 3) {
      tryPlay();
    } else {
      const onCanPlay = () => {
        audio.removeEventListener('canplay', onCanPlay);
        tryPlay();
      };
      audio.addEventListener('canplay', onCanPlay);
      audio.load();
    }
  };

  const pause = () => {
    console.log('pause', audioRef.current?.pause);
    setStatus(RecordStatus.Pause);
    audioRef.current?.pause();
  };

  const removeRecorder = (onChange: (value: Blob | undefined) => void): void => {
    pause();
    setAudioSrc(undefined);
    onChange(undefined);
    setStatus(RecordStatus.Ready);
    setTimer(0);
    clearTimer();
  };

  const transcribeVoiceRecord = async (voiceBlob: Blob): Promise<void> => {
    const voiceFile = new File([voiceBlob], 'audio.mp3', { type: 'audio/mpeg' });

    try {
      const res = await speechToText({ variables: { file: voiceFile } });

      const transcribedText = res.data?.speechToText?.trim();
      if (!transcribedText) {
        showErrorToast(t('assessment.question.voice.transcriptionError'));
        onTranscriptionError?.();
        return;
      }

      onTranscriptionSuccess?.(transcribedText);
    } catch {
      showErrorToast(t('assessment.question.voice.transcriptionError'));
      onTranscriptionError?.();
    }
  };

  const ffmpeg = new FFmpeg();

  const convertToMp3 = async (audioBlob: Blob): Promise<Blob> => {
    try {
      setStatus(RecordStatus.Converting);
      console.log('!ffmpeg.loaded', !ffmpeg.loaded);
      if (!ffmpeg.loaded) {
        await ffmpeg.load();
      }

      const mimeType = audioBlob.type;
      const extensionMap: Record<string, string> = {
        'audio/webm': 'webm',
        'audio/webm;codecs=opus': 'webm',
        'audio/ogg': 'ogg',
        'audio/ogg;codecs=opus': 'ogg',
        'audio/mp4': 'mp4',
      };

      const inputExt = extensionMap[mimeType] || 'webm';
      const inputFile = `input.${inputExt}`;
      console.log(mimeType);

      await ffmpeg.writeFile(inputFile, await fetchFile(audioBlob));
      await ffmpeg.exec([
        '-i',
        inputFile,
        '-acodec',
        'libmp3lame',
        '-qscale:a',
        '2',
        '-map_metadata',
        '-1',
        'output.mp3',
      ]);
      // await ffmpeg.exec([
      //   '-i',
      //   inputFile,
      //   '-vn', // отключить видео (если есть)
      //   // '-ar',
      //   // '44100', // частота дискретизации (CD-качество)
      //   '-acodec',
      //   'libmp3lame', //  указать MP3-кодек
      //   // '-qscale:a',
      //   // '2', //  качество VBR (~192 kbps)
      //   '-f',
      //   'mp3',
      //   '-map_metadata',
      //   '-1', // удалить метаданные
      //   'output.mp3',
      // ]);
      const data = await ffmpeg.readFile('output.mp3');
      if (ArrayBuffer.isView(data)) {
        return new Blob([data as BlobPart], { type: 'audio/mpeg' });
      }
      console.error('Unexpected file format: expected Uint8Array, got string');
      return audioBlob;
    } catch (error) {
      console.error('Error converting audio:', error);
      return audioBlob;
    }
  };

  const startRecord = async (onChange: (value: Blob | undefined) => void) => {
    const res = await initRecorder();
    if (!res) return;

    const { mediaStream, mediaRecorder } = res;
    const chunks: BlobPart[] = [];

    mediaRecorder.ondataavailable = (event) => {
      chunks.push(event.data);
    };

    mediaRecorder.onstop = async () => {
      mediaStream.getTracks().forEach((track) => track.stop());

      const audioBlob = new Blob(chunks, { type: mediaRecorder.mimeType || 'audio/webm' });
      console.log('audioBlob', audioBlob);
      convertToMp3(audioBlob)
        .then(async (resMp3Blob) => {
          onChange(resMp3Blob);
          const url = URL.createObjectURL(resMp3Blob);
          setAudioSrc(url);
          console.log('url', url);
          console.log('resMp3Blob', resMp3Blob);
          await transcribeVoiceRecord(resMp3Blob);
          setStatus(RecordStatus.Done);
        })
        .catch(() => {
          showErrorToast(t('assessment.question.voice.audioConversionError'));
          setStatus(RecordStatus.Done);
        });
    };

    mediaRecorder.start();
    initTimer();
    setStatus(RecordStatus.Recording);

    if (navigator.vibrate) navigator.vibrate(150);
  };

  const stopRecord = () => {
    recorder?.stop();
    clearTimer();

    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
  };

  useEffect(() => {
    const handleAudioTimeUpdate = () => {
      if (audioRef.current && status === RecordStatus.Playing) {
        setTimer(Math.floor(audioRef.current.currentTime));
      }
    };
    audioRef.current?.addEventListener('timeupdate', handleAudioTimeUpdate);

    return () => audioRef.current?.removeEventListener('timeupdate', handleAudioTimeUpdate);
  }, [audioRef.current, status]);

  return {
    audioSrc,
    status,
    audioRef,
    timer,
    isTranscribing,
    startRecord,
    stopRecord,
    play,
    pause,
    removeRecorder,
    transcribingError,
  };
};

export default useVoiceRecorded;
